import { useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import supportClient, { SupportConfig } from "@apis/supportClient";
import type { RootState } from "@/types/RootState";
import { translate } from "@/hooks/useLanguage";
import { LanguageKey } from "@/types/generated/LanguageKey";
import RightSideBar from "@cleanup/widgets/RightSideBar/RightSideBar";
import RightSidebarGroup from "@cleanup/widgets/RightSideBar/RightSidebarGroup";
import HelpSidebarItem from "./HelpSidebarItem";
import * as actions from "@/redux/actions";

import SntDocumentationCenterIcon from "@icons/SntDocumentationCenterIcon";
import SntReleaseNoteIcon from "@icons/SntReleaseNoteIcon";
import SntContactSupportIcon from "@icons/SntContactSupportIcon";
import PartnerIcon from "@images/common/partner.svg";

interface HelpSidebarProps {
  orgId?: number;
}

// Where the Sensolus documentation lives, independent of whatever the org
// configured. `support.helpdeskDocUrl` cannot stand in for it: the backend
// only fills that field with this value when the help page is left on
// "sensolus", and points it at the partner's own site for a custom Zendesk /
// Freshdesk (see Support.toObject).
const SENSOLUS_DOCUMENTATION_URL = "/rest/authentication/docs-login?redirect=/";

const HelpSidebar = ({ orgId }: HelpSidebarProps) => {
  const dispatch = useDispatch();
  const loginInfo = useSelector((state: RootState) => state.user, shallowEqual);
  const showHelpSidebar = useSelector(
    (state: RootState) => state.all.showHelpSidebar,
    shallowEqual
  );

  const [support, setSupport] = useState<SupportConfig>({
    helpPageSupportType: "sensolus",
    platformPlansSupportType: "sensolus",
    isHiddenSensolusLink: false,
    helpdeskType: "ZENDESK_SENSOLUS",
    helpdeskBaseUrl: "https://sensolussupport.zendesk.com",
    helpdeskDocUrl: "/rest/authentication/docs-login?redirect=/",
    helpdeskTicketUrl: "https://sensolussupport.zendesk.com/hc/en-us/requests/new",
  });

  useEffect(() => {
    if (orgId) {
      supportClient
        .get(orgId)
        .then(({ data }) => {
          setSupport(data);
        })
        .catch(() => {
          // Keep default state on error
        });
    }
  }, [orgId]);

  const handleClose = () => {
    dispatch(actions.setShowHelpSidebar(false));
  };

  if (!showHelpSidebar) {
    return null;
  }

  const isPartnerLogin = loginInfo?.organisation?.organisationType === "PARTNER";
  const isCommercialPartner =
    isPartnerLogin && !!loginInfo?.partner?.commercialPartner;

  // A partner's own support settings are inherited by every organisation under
  // it, the partner's organisation included. Customising them therefore takes
  // the Sensolus documentation and helpdesk away from the partner's own users
  // too — and they are precisely the people who still need to reach us
  // (STIC-16239). So for a partner login the Sensolus blocks are kept, below
  // the custom ones instead of replaced by them. Customers under the partner
  // keep seeing only what the partner configured.
  const hasCustomHelpPage = support.helpPageSupportType === "custom";
  const hasCustomHelpdesk = support.helpdeskType === "CUSTOM_SUPPORT";
  const showSensolusHelpPage = !hasCustomHelpPage || isPartnerLogin;
  const showSensolusHelpdesk = !hasCustomHelpdesk || isPartnerLogin;

  const getContactSupportLink = (): string | { pathname: string } => {
    if (isPartnerLogin) {
      return `/zendesk/sso?action=sidebar_click&menu=ticket&useDefaultHelpDesk=true&org=${loginInfo?.organisation?.id}`;
    }
    if (support.helpdeskType === "FRESHDESK") {
      return { pathname: support.helpdeskTicketUrl || "" };
    }
    return `/zendesk/sso?action=sidebar_click&menu=ticket&org=${loginInfo?.organisation?.id}`;
  };

  return (
    <RightSideBar
      title={translate(LanguageKey.help_and_support)}
      onClose={handleClose}
      loading={false}
    >
      {/* Custom Help Page */}
      {hasCustomHelpPage && (
        <RightSidebarGroup
          icon={<SntDocumentationCenterIcon size={24} color="#424B59" />}
          title={support.helpPageTitle || ""}
        >
          <HelpSidebarItem htmlContent={support.helpPageDescription || ""} />
        </RightSidebarGroup>
      )}

      {/* Documentation Center - Sensolus */}
      {showSensolusHelpPage && (
        <RightSidebarGroup
          icon={<SntDocumentationCenterIcon size={24} color="#424B59" />}
          // Named after its owner when it sits next to the partner's own
          // documentation block, so the two are told apart.
          title={translate(
            hasCustomHelpPage
              ? LanguageKey.sensolus_support_key
              : LanguageKey.documentation_center_key
          )}
          showDivider={hasCustomHelpPage}
        >
          <HelpSidebarItem
            description={translate(LanguageKey.detail_information_platform_guide)}
            links={[
              {
                text: translate(LanguageKey.home_key),
                url: {
                  pathname: hasCustomHelpPage
                    ? SENSOLUS_DOCUMENTATION_URL
                    : support.helpdeskDocUrl || SENSOLUS_DOCUMENTATION_URL,
                },
              },
              ...(isCommercialPartner
                ? [
                    {
                      text: translate(LanguageKey.partner_resources_key),
                      url: "/rest/authentication/docs-login?redirect=/docs/partner/partner_resource_center",
                    },
                  ]
                : []),
            ]}
          />
        </RightSidebarGroup>
      )}

      {/* Release Notes - Sensolus */}
      {showSensolusHelpPage && (
        <RightSidebarGroup
          icon={<SntReleaseNoteIcon size={24} color="#424B59" />}
          title={translate(LanguageKey.release_notes)}
          showDivider
        >
          <HelpSidebarItem
            description={translate(LanguageKey.find_out_whats_new_in_the_platform)}
            linkText={translate(LanguageKey.read_the_release_notes)}
            linkUrl="/rest/authentication/docs-login?redirect=/docs/releases/release_notes"
          />
        </RightSidebarGroup>
      )}

      {/* Impartner Portal */}
      {isCommercialPartner && (
        <RightSidebarGroup
          icon={<img src={PartnerIcon} width="24" height="24" alt="" />}
          title={translate(LanguageKey.impartner_portal_key)}
          showDivider
        >
          <HelpSidebarItem
            description={translate(LanguageKey.partner_portal_description_key)}
            linkText={translate(LanguageKey.visit_the_impartner_portal_key)}
            linkUrl={{ pathname: "https://impartner.sensolus.com" }}
          />
        </RightSidebarGroup>
      )}

      {/* Contact Support - Custom */}
      {hasCustomHelpdesk && (
        <RightSidebarGroup
          icon={<SntContactSupportIcon size={24} color="#424B59" />}
          title={support.helpdeskTitle || ""}
          showDivider
        >
          <HelpSidebarItem htmlContent={support.helpdeskDescription || ""} />
        </RightSidebarGroup>
      )}

      {/* Contact Support - Sensolus */}
      {showSensolusHelpdesk && (
        <RightSidebarGroup
          icon={<SntContactSupportIcon size={24} color="#424B59" />}
          title={translate(LanguageKey.contact_support_key)}
          showDivider
        >
          <HelpSidebarItem
            description={translate(LanguageKey.do_you_have_questions_remarks)}
            linkText={translate(LanguageKey.make_a_request)}
            linkUrl={getContactSupportLink()}
          />
        </RightSidebarGroup>
      )}
    </RightSideBar>
  );
};

export default HelpSidebar;
